import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FileText, Download, Wallet, CheckCircle2, Clock, MessageCircle, FileSignature,
  ExternalLink, Truck, ShieldCheck, LogOut, Mail, KeyRound, FolderArchive,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { openWaNotifyPayment } from "@/lib/whatsapp";

const money = (n, ccy) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ${ccy}`;

const STR = {
  en: {
    loading: "Loading portal…",
    not_found: "Portal not found",
    not_found_sub: "This portal link is invalid or has been revoked. Please contact KINOS at info@kinos.tl for a fresh link.",
    client_portal: "KINOS · Client Portal",
    secure_access: "secure access",
    secure_access_solo: "Secure access",
    protected_msg: "This portal is protected. Verify your email with a one-time code to continue.",
    email_on_file: "Email on file",
    email_me_code: "Email me a login code",
    sending: "Sending…",
    code_sent_1: "We sent a 6-digit code to",
    code_sent_2: "It expires in 10 minutes.",
    open_portal: "Open my portal",
    verifying: "Verifying…",
    diff_email: "Use a different email",
    trouble: "Trouble logging in? Email",
    welcome_back: "Welcome back",
    sign_out: "Sign out",
    no_invoices_yet: "No invoices on file yet.",
    outstanding: "Outstanding",
    unpaid_lc: "unpaid",
    total_invoices: "total invoices",
    paid_lc: "paid",
    quotes_awaiting: "Quotations awaiting your decision",
    quotation_no: "Quotation #",
    issue: "Issue",
    valid_until: "Valid until",
    total: "Total",
    review_approve: "Review & approve",
    pending_link: "Pending link",
    your_deliveries: "Your deliveries",
    issued: "Issued",
    delivered: "Delivered",
    lines: "Lines",
    items: "Items",
    status: "Status",
    your_invoices: "Your invoices",
    zip_outstanding: "Outstanding (ZIP)",
    zip_all: "All invoices (ZIP)",
    invoice_no: "Invoice #",
    due: "Due",
    paid_col: "Paid",
    balance: "Balance",
    no_invoices_show: "No invoices to show.",
    ive_paid: "I've paid",
    ive_paid_title: "Notify KINOS on WhatsApp that you've paid",
    st_paid: "Paid",
    st_partial: "Partial",
    st_unpaid: "Unpaid",
    powered: "Powered by KINOS · Snapshot generated",
    questions: "Questions? Email",
    dl_started: "Download started…",
    dl_none: "No invoices to download for this selection",
    dl_failed: "Download failed — please try again",
  },
  tet: {
    loading: "Karrega hela portál…",
    not_found: "Portál la hetan",
    not_found_sub: "Ligasaun portál ida-ne'e la vále ka kansela tiha ona. Favór kontaktu KINOS iha info@kinos.tl atu hetan ligasaun foun.",
    client_portal: "KINOS · Portál Kliente",
    secure_access: "asesu seguru",
    secure_access_solo: "Asesu seguru",
    protected_msg: "Portál ida-ne'e proteje. Verifika Ita-nia email ho kódigu uza-dala-ida atu kontinua.",
    email_on_file: "Email iha rejistu",
    email_me_code: "Haruka kódigu ba ha'u-nia email",
    sending: "Haruka hela…",
    code_sent_1: "Ami haruka kódigu dijitu-6 ba",
    code_sent_2: "Nia sei espira iha minutu 10.",
    open_portal: "Loke ha'u-nia portál",
    verifying: "Verifika hela…",
    diff_email: "Uza email seluk",
    trouble: "Susar atu tama? Haruka email ba",
    welcome_back: "Bem-vindu fila fali",
    sign_out: "Sai",
    no_invoices_yet: "Seidauk iha fatura.",
    outstanding: "Restu atu selu",
    unpaid_lc: "seidauk selu",
    total_invoices: "fatura hotu",
    paid_lc: "selu ona",
    quotes_awaiting: "Kotasaun sira ne'ebé hein Ita-nia desizaun",
    quotation_no: "Kotasaun #",
    issue: "Emisaun",
    valid_until: "Válidu to'o",
    total: "Totál",
    review_approve: "Haree no aprova",
    pending_link: "Ligasaun pendente",
    your_deliveries: "Ita-nia entrega sira",
    issued: "Emitidu",
    delivered: "Entrega ona",
    lines: "Liña",
    items: "Sasán",
    status: "Estadu",
    your_invoices: "Ita-nia fatura sira",
    zip_outstanding: "Restu (ZIP)",
    zip_all: "Fatura hotu (ZIP)",
    invoice_no: "Fatura #",
    due: "Vensimentu",
    paid_col: "Selu ona",
    balance: "Saldu",
    no_invoices_show: "La iha fatura atu hatudu.",
    ive_paid: "Ha'u selu tiha ona",
    ive_paid_title: "Avizu KINOS liuhusi WhatsApp katak Ita selu tiha ona",
    st_paid: "Selu ona",
    st_partial: "Parsiál",
    st_unpaid: "Seidauk selu",
    powered: "Husi KINOS · Jera iha",
    questions: "Pergunta? Haruka email ba",
    dl_started: "Download komesa ona…",
    dl_none: "La iha fatura atu download",
    dl_failed: "Download la konsege — favór koko fali",
  },
};

const sessionKey = (token) => `kinos_portal_session_${token}`;
const LANG_KEY = "kinos_portal_lang";

function usePortalLang() {
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) === "tet" ? "tet" : "en");
  const setLang = (l) => {
    localStorage.setItem(LANG_KEY, l);
    setLangState(l);
  };
  const t = (k) => STR[lang][k] ?? STR.en[k] ?? k;
  return { lang, setLang, t };
}

function LangSwitch({ lang, setLang, dark = false }) {
  const base = "px-2.5 py-1 text-[10px] font-bold tracking-wider rounded-full transition-colors";
  const activeCls = "bg-primary text-white";
  const idleCls = dark
    ? "text-white/60 hover:text-white"
    : "text-muted-foreground hover:text-foreground";
  return (
    <div
      data-testid="portal-lang-switch"
      className={`inline-flex items-center rounded-full p-0.5 ${dark ? "bg-white/10" : "bg-slate-100 border border-border"}`}
    >
      <button
        type="button"
        data-testid="portal-lang-en"
        onClick={() => setLang("en")}
        className={`${base} ${lang === "en" ? activeCls : idleCls}`}
      >
        EN
      </button>
      <button
        type="button"
        data-testid="portal-lang-tet"
        onClick={() => setLang("tet")}
        className={`${base} ${lang === "tet" ? activeCls : idleCls}`}
      >
        TET
      </button>
    </div>
  );
}

/**
 * Public Client Portal — /portal/<token>.
 * Supports an optional email + one-time-code login wall and a one-tap
 * English / Tetum language switch (persisted in localStorage).
 */
export default function ClientPortal() {
  const { token } = useParams();
  const { lang, setLang, t } = usePortalLang();
  const [session, setSession] = useState(() => localStorage.getItem(sessionKey(token)) || "");

  const STATUS_STYLE = {
    paid: { label: t("st_paid"), cls: "bg-emerald-100 text-emerald-800 ring-emerald-200", Icon: CheckCircle2 },
    partial: { label: t("st_partial"), cls: "bg-amber-100 text-amber-800 ring-amber-200", Icon: Wallet },
    unpaid: { label: t("st_unpaid"), cls: "bg-red-100 text-red-800 ring-red-200", Icon: Clock },
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-portal", token, session],
    queryFn: () => api.getPublicClientPortal(token, session),
    retry: false,
  });

  const status = error?.response?.status;
  const loginRequired = status === 401 && error?.response?.data?.login_required;

  // A stale/revoked session came back 401 — drop it so the login form shows.
  useEffect(() => {
    if (loginRequired && session) {
      localStorage.removeItem(sessionKey(token));
      setSession("");
    }
  }, [loginRequired, session, token]);

  function handleLoggedIn(sessToken) {
    localStorage.setItem(sessionKey(token), sessToken);
    setSession(sessToken);
  }
  function signOut() {
    localStorage.removeItem(sessionKey(token));
    setSession("");
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <p className="text-muted-foreground text-sm">{t("loading")}</p>
      </div>
    );
  }

  if (loginRequired) {
    return (
      <PortalLogin
        token={token}
        clientName={error?.response?.data?.client_name || ""}
        onLoggedIn={handleLoggedIn}
        lang={lang}
        setLang={setLang}
        t={t}
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-6">
        <div className="max-w-md text-center">
          <div className="mb-4 flex justify-center">
            <LangSwitch lang={lang} setLang={setLang} />
          </div>
          <h1 className="font-display text-2xl font-bold mb-2">{t("not_found")}</h1>
          <p className="text-sm text-muted-foreground">{t("not_found_sub")}</p>
        </div>
      </div>
    );
  }

  const client = data?.client || {};
  const totals = data?.totals_by_currency || {};
  const invoices = data?.invoices || [];
  const pendingQuotes = data?.quotations_pending || [];
  const deliveryOrders = data?.delivery_orders || [];
  const sessionEmail = data?.session_email || "";
  const hasOutstanding = invoices.some((i) => Number(i.balance_due || 0) > 0);
  const totalsLine = Object.entries(totals)
    .map(([c, tt]) => ({ currency: c, ...tt }))
    .sort((a, b) => b.outstanding - a.outstanding);

  async function downloadZip(scope) {
    try {
      await api.downloadPortalInvoicesZip(token, scope, session);
      toast.success(t("dl_started"));
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.info(t("dl_none"));
      } else {
        toast.error(t("dl_failed"));
      }
    }
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal-page">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <header
        style={{ backgroundColor: "#0A0A0A", borderBottom: "3px solid #C8102E" }}
        className="text-white"
      >
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">{t("client_portal")}</div>
              <h1 className="font-display text-3xl font-bold mt-1 text-white" data-testid="portal-client-name">
                {client.name}
              </h1>
              {(client.contact_person || client.email) && (
                <p className="text-sm text-white/70 mt-1">
                  {client.contact_person}
                  {client.contact_person && client.email ? " · " : ""}
                  {client.email}
                </p>
              )}
            </div>
            <div className="text-right shrink-0 flex flex-col items-end gap-2">
              <LangSwitch lang={lang} setLang={setLang} dark />
              {sessionEmail && (
                <>
                  <span
                    data-testid="portal-session-chip"
                    className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-400/30"
                  >
                    <ShieldCheck className="size-3.5" />
                    {t("welcome_back")} · {sessionEmail}
                  </span>
                  <button
                    type="button"
                    data-testid="portal-signout-btn"
                    onClick={signOut}
                    className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white"
                  >
                    <LogOut className="size-3" /> {t("sign_out")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-8">
        {/* Totals */}
        <section data-testid="portal-totals" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {totalsLine.length === 0 ? (
            <div className="col-span-3 rounded-md bg-white border border-border p-5 text-center text-sm text-muted-foreground">
              {t("no_invoices_yet")}
            </div>
          ) : (
            totalsLine.map((tt) => (
              <div
                key={tt.currency}
                data-testid={`portal-total-${tt.currency}`}
                className="rounded-md bg-white border border-border p-4"
              >
                <div className="overline text-muted-foreground text-[10px]">
                  {t("outstanding")} · {tt.currency}
                </div>
                <div className={`mono text-3xl font-bold mt-1 ${tt.outstanding > 0 ? "text-primary" : "text-emerald-600"}`}>
                  {money(tt.outstanding, tt.currency).replace(` ${tt.currency}`, "")}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  <span className="mono">{tt.outstanding_count}</span> {t("unpaid_lc")} ·
                  <span className="mono ml-1">{tt.count}</span> {t("total_invoices")}
                  {tt.paid > 0 && (
                    <> · <span className="mono">{money(tt.paid, tt.currency).replace(` ${tt.currency}`, "")}</span> {t("paid_lc")}</>
                  )}
                </div>
              </div>
            ))
          )}
        </section>

        {/* Pending quotations */}
        {pendingQuotes.length > 0 && (
          <section data-testid="portal-quotes-section">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                <FileSignature className="size-4 text-amber-700" />
                {t("quotes_awaiting")}
                <span className="text-[10px] mono px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  {pendingQuotes.length}
                </span>
              </h2>
            </div>
            <div className="overflow-x-auto bg-white border border-amber-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-amber-50 text-[10px] uppercase tracking-wider text-amber-800">
                  <tr>
                    <th className="text-left px-4 py-3">{t("quotation_no")}</th>
                    <th className="text-left px-4 py-3">{t("issue")}</th>
                    <th className="text-left px-4 py-3">{t("valid_until")}</th>
                    <th className="text-right px-4 py-3">{t("total")}</th>
                    <th className="w-40"></th>
                  </tr>
                </thead>
                <tbody>
                  {pendingQuotes.map((q) => (
                    <tr key={q.id} data-testid={`portal-quote-${q.id}`} className="border-t border-amber-100">
                      <td className="px-4 py-3 mono font-medium">{q.quotation_number}</td>
                      <td className="px-4 py-3 mono text-xs">{q.issue_date || "—"}</td>
                      <td className="px-4 py-3 mono text-xs">{q.valid_until || "—"}</td>
                      <td className="px-4 py-3 mono text-right font-bold">{money(q.total, q.currency)}</td>
                      <td className="px-4 py-3 text-right">
                        {q.public_token ? (
                          <a
                            data-testid={`portal-quote-approve-${q.id}`}
                            href={`/approve/${q.public_token}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90"
                          >
                            {t("review_approve")} <ExternalLink className="size-3" />
                          </a>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">{t("pending_link")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Delivery orders */}
        {deliveryOrders.length > 0 && (
          <section data-testid="portal-do-section">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-display text-lg font-semibold flex items-center gap-2">
                <Truck className="size-4 text-primary" />
                {t("your_deliveries")}
                <span className="text-[10px] mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {deliveryOrders.length}
                </span>
              </h2>
            </div>
            <div className="overflow-x-auto bg-white border border-border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">DO #</th>
                    <th className="text-left px-4 py-3">{t("issued")}</th>
                    <th className="text-left px-4 py-3">{t("delivered")}</th>
                    <th className="text-right px-4 py-3">{t("lines")}</th>
                    <th className="text-right px-4 py-3">{t("items")}</th>
                    <th className="text-left px-4 py-3">{t("status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryOrders.map((d) => {
                    const doneStates = ["delivered", "completed"];
                    const tone = doneStates.includes(d.status)
                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                      : d.status === "in_transit"
                      ? "bg-sky-50 text-sky-700 ring-sky-200"
                      : "bg-slate-50 text-slate-700 ring-slate-200";
                    return (
                      <tr
                        key={d.id}
                        data-testid={`portal-do-${d.id}`}
                        className="border-t border-border"
                      >
                        <td className="px-4 py-3 mono font-medium">{d.do_number}</td>
                        <td className="px-4 py-3 mono text-xs">{d.issue_date || "—"}</td>
                        <td className="px-4 py-3 mono text-xs">{d.delivered_at || "—"}</td>
                        <td className="px-4 py-3 mono text-right">{d.line_count}</td>
                        <td className="px-4 py-3 mono text-right">{d.total_items}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${tone}`}
                          >
                            {d.status.replace(/_/g, " ")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Invoices */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-display text-lg font-semibold">{t("your_invoices")}</h2>
            <div className="flex items-center gap-2">
              {hasOutstanding && (
                <button
                  type="button"
                  data-testid="portal-zip-outstanding"
                  onClick={() => downloadZip("outstanding")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90"
                >
                  <FolderArchive className="size-3.5" /> {t("zip_outstanding")}
                </button>
              )}
              {invoices.length > 0 && (
                <button
                  type="button"
                  data-testid="portal-zip-all"
                  onClick={() => downloadZip("all")}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-border bg-white text-foreground hover:bg-slate-50"
                >
                  <FolderArchive className="size-3.5" /> {t("zip_all")}
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto bg-white border border-border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">{t("invoice_no")}</th>
                  <th className="text-left px-4 py-3">{t("issue")}</th>
                  <th className="text-left px-4 py-3">{t("due")}</th>
                  <th className="text-right px-4 py-3">{t("total")}</th>
                  <th className="text-right px-4 py-3">{t("paid_col")}</th>
                  <th className="text-right px-4 py-3">{t("balance")}</th>
                  <th className="text-left px-4 py-3">{t("status")}</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-10 text-muted-foreground">
                      {t("no_invoices_show")}
                    </td>
                  </tr>
                ) : (
                  invoices.map((inv) => {
                    const s = STATUS_STYLE[inv.payment_status] || STATUS_STYLE.unpaid;
                    const S = s.Icon;
                    const pdfUrl = inv.public_token
                      ? api.publicInvoicePdfUrl(inv.public_token)
                      : null;
                    return (
                      <tr key={inv.id} data-testid={`portal-inv-${inv.id}`} className="border-t border-border">
                        <td className="px-4 py-3 mono font-medium">{inv.invoice_number}</td>
                        <td className="px-4 py-3 mono text-xs">{inv.issue_date || "—"}</td>
                        <td className="px-4 py-3 mono text-xs">{inv.due_date || "—"}</td>
                        <td className="px-4 py-3 mono text-right">{money(inv.total, inv.currency)}</td>
                        <td className="px-4 py-3 mono text-right text-muted-foreground">
                          {money(inv.paid_amount, inv.currency)}
                        </td>
                        <td className="px-4 py-3 mono text-right font-bold text-primary">
                          {money(inv.balance_due, inv.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset ${s.cls}`}>
                            <S className="size-3" /> {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2">
                            {inv.balance_due > 0 && (
                              <button
                                type="button"
                                data-testid={`portal-notify-pay-${inv.id}`}
                                onClick={() => openWaNotifyPayment({
                                  clientName: client.name,
                                  docNum: inv.invoice_number,
                                  amount: inv.balance_due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                                  currency: inv.currency,
                                })}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline whitespace-nowrap"
                                title={t("ive_paid_title")}
                              >
                                <MessageCircle className="size-3.5" />
                                {t("ive_paid")}
                              </button>
                            )}
                            {pdfUrl ? (
                              <a
                                data-testid={`portal-download-${inv.id}`}
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                title="PDF"
                              >
                                <Download className="size-3.5" />
                                <FileText className="size-3.5" />
                              </a>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="text-center text-[11px] text-muted-foreground pt-4 pb-8">
          {t("powered")} {new Date(data?.generated_at || Date.now()).toLocaleString()} ·{" "}
          {t("questions")} <a className="text-primary hover:underline" href="mailto:info@kinos.tl">info@kinos.tl</a>
        </footer>
      </main>
    </div>
  );
}

/** Two-step email + one-time-code login wall. */
function PortalLogin({ token, clientName, onLoggedIn, lang, setLang, t }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function requestCode(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.requestPortalCode(token, email.trim());
      setStep("code");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not send code — try again");
    } finally {
      setBusy(false);
    }
  }

  async function verify(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.verifyPortalCode(token, email.trim(), code.trim());
      onLoggedIn(r.session_token);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Verification failed — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-6" data-testid="portal-login-page">
      <div className="w-full max-w-sm">
        <div className="rounded-lg overflow-hidden border border-border bg-white shadow-sm">
          <div style={{ backgroundColor: "#0A0A0A", borderBottom: "3px solid #C8102E" }} className="px-6 py-5 text-white">
            <div className="flex items-start justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">{t("client_portal")}</div>
              <LangSwitch lang={lang} setLang={setLang} dark />
            </div>
            <h1 className="font-display text-xl font-bold mt-1">
              {clientName ? `${clientName} — ${t("secure_access")}` : t("secure_access_solo")}
            </h1>
          </div>
          <div className="p-6">
            <p className="text-xs text-muted-foreground mb-4 flex items-start gap-2">
              <ShieldCheck className="size-4 text-emerald-600 shrink-0 mt-0.5" />
              {t("protected_msg")}
            </p>

            {step === "email" ? (
              <form onSubmit={requestCode} className="space-y-3">
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {t("email_on_file")}
                </label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <input
                    data-testid="portal-login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full h-10 pl-8 pr-3 rounded-md border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <button
                  data-testid="portal-login-request-btn"
                  type="submit"
                  disabled={busy}
                  className="w-full h-10 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                >
                  {busy ? t("sending") : t("email_me_code")}
                </button>
              </form>
            ) : (
              <form onSubmit={verify} className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {t("code_sent_1")} <span className="mono font-medium">{email}</span>. {t("code_sent_2")}
                </p>
                <div className="relative">
                  <KeyRound className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <input
                    data-testid="portal-login-code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="w-full h-10 pl-8 pr-3 rounded-md border border-border text-sm mono tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <button
                  data-testid="portal-login-verify-btn"
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="w-full h-10 rounded-md bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                >
                  {busy ? t("verifying") : t("open_portal")}
                </button>
                <button
                  data-testid="portal-login-back-btn"
                  type="button"
                  onClick={() => { setStep("email"); setCode(""); setErr(""); }}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  {t("diff_email")}
                </button>
              </form>
            )}

            {err && (
              <p data-testid="portal-login-error" className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {err}
              </p>
            )}
          </div>
        </div>
        <p className="text-center text-[11px] text-muted-foreground mt-4">
          {t("trouble")} <a className="text-primary hover:underline" href="mailto:info@kinos.tl">info@kinos.tl</a>
        </p>
      </div>
    </div>
  );
}
